import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import DocumentRepository, { DocumentInfo } from "../../Repositories/DocumentRepository";
import QuoteRepository, { QuoteInfo } from "../../Repositories/QuoteRepository";
import { SUPPORT_SUBJECT_COURSE_ISSUE } from "../../config/supportContact";
import { describeCourseForSupport } from "../../Components/App/Courses/CourseReportCta";
import Functions from "../../Helpers/Functions";
import { DV2 } from "../DeclarationV2/theme";
import { clock, dayLabel, euros, hours as formatHours, plural } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { Avatar, Button, Card, TextLink, useDv2Theme } from "../DeclarationV2/layout/Ui";
import { clientHelpPath } from "./clientPaths";
import { courseDatesLabel, isUndetailedMonthCourse } from "../CoursesV2/courseRows";
import { courseAdvance, courseChildName, courseFormatLabel, courseHours, coursePaid, courseTeachrInitials, courseTeachrName, courseTotalTtc } from "./clientCourseRows";

/**
 * Fiche d'un cours côté client (espace client repensé, 2 novembre 2026), dans
 * la fenêtre de « Mes cours » : l'en-tête remplace le titre (professeur,
 * statut, résumé), le corps montre ce que paie le client, les séances, le
 * déroulement, la note du professeur, les documents du cours (devis, facture)
 * et le lien pour signaler un problème. Le client ne voit ni la rémunération
 * du professeur ni les frais de gestion.
 */

export interface ClientCourseDetailProps {
    course: any;
    /** Avance immédiate active pour ce client : la moitié est réglée par l'URSSAF. */
    aiciActive: boolean;
    /** Pastille de statut avec son explication (`clientCourseStatusDetail`). */
    statusNode: React.ReactNode;
    /** Identifiant du client connecté, pour retrouver la facture du cours. */
    clientId?: number;
}

const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value);

/** « Anglais avec Jean Dupont », « Cours avec Jean Dupont » chez Verified Trainer. */
const courseTitle = (course: any): string => {
    const subject = course?.subjectAsked ? capitalize(course.subjectAsked) : capitalize(vocab.course());
    return `${subject} avec ${courseTeachrName(course)}`;
};

/** « Cours ponctuel, 1 h, jeudi 15 octobre 2026 à 07h30 » ou « Plusieurs cours, 2 séances, 3 h au total, du … au … ». */
const courseSummary = (course: any): string => {
    const sessions: Array<{ date: string; durationMinutes: number }> = Array.isArray(course?.sessions) ? course.sessions : [];
    const start = course?.startDatetime ? moment(course.startDatetime) : null;
    const end = course?.endDatetime ? moment(course.endDatetime) : null;
    const period = start && end
        ? (start.isSame(end, "day") ? dayLabel(start, true).toLowerCase() : `du ${dayLabel(start).toLowerCase()} au ${dayLabel(end, true).toLowerCase()}`)
        : "";
    if (isUndetailedMonthCourse(course)) {
        return `${vocab.multipleCoursesTitle()}, séances non détaillées, ${formatHours(courseHours(course))} au total, ${period}`;
    }
    if (sessions.length > 1) {
        return `${vocab.multipleCoursesTitle()}, ${plural(sessions.length, "séance")}, ${formatHours(courseHours(course))} au total, ${period}`;
    }
    return `${courseFormatLabel(course)}, ${formatHours(courseHours(course))}, ${period}${start ? ` à ${clock(start)}` : ""}`;
};

/** En-tête de la fenêtre : avatar du professeur, titre, statut, résumé. */
export const ClientCourseDetailHeader = ({ course, statusNode }: { course: any; statusNode: React.ReactNode }) => {
    const { fontFamily, text } = useDv2Theme();
    const child = courseChildName(course);
    return (
        <div className="flex items-start gap-3" style={{ fontFamily, color: text, fontWeight: 400, paddingRight: 8 }}>
            <Avatar text={courseTeachrInitials(course)} size={44} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>{courseTitle(course)}{child ? <span style={{ fontWeight: 500, color: DV2.muted }}>, pour {child}</span> : null}</div>
                <div style={{ marginTop: 6 }}>{statusNode}</div>
                <div style={{ fontSize: 13, color: DV2.muted, marginTop: 6, lineHeight: 1.4 }}>{courseSummary(course)}</div>
            </div>
        </div>
    );
};

const AmountTile = ({ label, value, hint, highlight }: { label: string; value: string; hint?: React.ReactNode; highlight?: boolean }) => {
    const { primary } = useDv2Theme();
    return (
        <Card style={highlight ? { background: primary, border: "none", color: "#fff", padding: "16px 18px" } : { padding: "16px 18px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: highlight ? undefined : DV2.muted, opacity: highlight ? 0.85 : 1 }}>{label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{value}</div>
            {hint && <div style={{ fontSize: 12, color: highlight ? undefined : DV2.muted, opacity: highlight ? 0.85 : 1, lineHeight: 1.45, marginTop: 2 }}>{hint}</div>}
        </Card>
    );
};

const DocumentRow = ({ title, detail, action }: { title: React.ReactNode; detail: React.ReactNode; action: React.ReactNode }) => {
    const { primary, fontFamily } = useDv2Theme();
    return (
        <div className="flex items-center gap-3" style={{ padding: "12px 0", borderTop: `1px solid ${DV2.borderSoft}`, fontFamily }}>
            <div aria-hidden style={{ width: 40, height: 48, borderRadius: 8, background: DV2.tint, border: "1px solid #C9D9EC", display: "grid", placeItems: "center", fontSize: 10, fontWeight: 800, color: primary, flexShrink: 0 }}>PDF</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{title}</div>
                <div style={{ fontSize: 12, color: DV2.muted }}>{detail}</div>
            </div>
            <div style={{ flexShrink: 0 }}>{action}</div>
        </div>
    );
};

const Accordion = ({ id, title, hint, children }: { id: string; title: string; hint?: React.ReactNode; children: React.ReactNode }) => {
    const { fontFamily, text } = useDv2Theme();
    const [open, setOpen] = useState(false);
    return (
        <Card padding={0} style={{ overflow: "hidden" }}>
            <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls={`${id}-panel`} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 20px", background: "none", border: "none", cursor: "pointer", fontFamily, color: text, textAlign: "left" }}>
                <span style={{ minWidth: 0 }}>
                    <span style={{ display: "block", fontWeight: 800, fontSize: 15 }}>{title}</span>
                    {hint && <span style={{ display: "block", fontSize: 12, color: DV2.muted, marginTop: 2 }}>{hint}</span>}
                </span>
                <span aria-hidden style={{ fontSize: 18, color: DV2.placeholder, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", flexShrink: 0 }}>⌄</span>
            </button>
            {open && (
                <div id={`${id}-panel`} style={{ padding: "0 20px 16px", borderTop: `1px solid ${DV2.borderSoft}` }}>
                    <div style={{ paddingTop: 12 }}>{children}</div>
                </div>
            )}
        </Card>
    );
};

const ClientCourseDetailV2 = ({ course, aiciActive, clientId }: ClientCourseDetailProps) => {
    const { fontFamily, text, brandName } = useDv2Theme();
    const navigate = useNavigate();

    // Devis du cours (celui du client) et facture, quand ils existent.
    const [quote, setQuote] = useState<QuoteInfo | null>(null);
    const [invoice, setInvoice] = useState<DocumentInfo | null>(null);
    useEffect(() => {
        if (!course?.id) return undefined;
        let cancelled = false;
        QuoteRepository.getQuoteForCourse(course.id)
            .then((response) => { if (!cancelled) setQuote(response?.quote ?? null); })
            .catch(() => undefined);
        if (clientId) {
            DocumentRepository.getClientDocuments(clientId)
                .then((response) => {
                    if (cancelled) return;
                    // Le document du client est le récapitulatif de facturation ; la
                    // facture du prof ne sert que si le cours n'en a pas (ancienne facturation).
                    const ofCourse = (response?.documents ?? []).filter((d) => d.courseId === course.id);
                    const found = ofCourse.find((d) => d.type === "invoice_recap") ?? ofCourse.find((d) => d.type === "invoice");
                    setInvoice(found ?? null);
                })
                .catch(() => undefined);
        }
        return () => { cancelled = true; };
    }, [course?.id, clientId]);

    const openQuote = () => {
        if (!quote) return;
        QuoteRepository.download(quote.id)
            .then((response) => { if (response?.url) window.open(response.url, "_blank", "noopener"); })
            .catch(() => undefined);
    };

    const total = courseTotalTtc(course);
    const advance = courseAdvance(course, aiciActive);
    const paid = coursePaid(course, aiciActive);
    const sessions: Array<{ id?: number; date: string; durationMinutes: number }> = Array.isArray(course?.sessions) ? course.sessions : [];
    const start = course?.startDatetime ? moment(course.startDatetime) : null;
    const end = course?.endDatetime ? moment(course.endDatetime) : null;
    const teachrName = courseTeachrName(course);

    const reportIssue = () => navigate(clientHelpPath(), { state: { subject: SUPPORT_SUBJECT_COURSE_ISSUE, context: describeCourseForSupport(course) } });

    return (
        <div style={{ fontFamily, color: text }} className="flex flex-col gap-4">
            {/* Ce que paie le client, jamais la part du professeur ni les frais */}
            <div className={`grid grid-cols-1 gap-3 ${aiciActive ? "sm:grid-cols-3" : "sm:grid-cols-1"}`}>
                <AmountTile label={aiciActive ? "Vous payez" : "Prix total"} value={euros(paid)} hint={aiciActive ? "avance immédiate déduite" : `TTC, pour ${formatHours(courseHours(course))}`} highlight />
                {aiciActive && (
                    <>
                        <AmountTile label={`Prix total ${vocab.ofTheCourse()}`} value={euros(total)} hint={`TTC, pour ${formatHours(courseHours(course))}`} />
                        <AmountTile label="Pris en charge par l'URSSAF" value={euros(advance)} hint="avance immédiate du crédit d'impôt, 50 %" />
                    </>
                )}
            </div>

            {/* Séances */}
            <Card style={{ padding: "16px 20px" }}>
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>{sessions.length > 1 || isUndetailedMonthCourse(course) ? "Séances" : "Séance"}</div>
                {isUndetailedMonthCourse(course) ? (
                    <div className="flex items-center gap-3" style={{ padding: "10px 0", borderTop: `1px solid ${DV2.borderSoft}` }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 800, fontSize: 14 }}>{courseDatesLabel(course)}</div>
                            <div style={{ fontSize: 12, color: DV2.muted }}>Séances non détaillées : avec l'ancienne déclaration, {vocab.yourTeachr()} déclarait les {vocab.courses()} du mois sur une période globale, sans le détail des jours. Les prochaines déclarations enregistrent chaque séance, qui s'affichera ici une par une.</div>
                        </div>
                        <div style={{ fontSize: 13, color: DV2.muted, minWidth: 56, textAlign: "right" }}>{formatHours(courseHours(course))} au total</div>
                    </div>
                ) : sessions.length > 0 ? sessions.map((session, index) => {
                    const s = moment(session.date);
                    const e = s.clone().add(session.durationMinutes ?? 0, "minutes");
                    return (
                        <div key={session.id ?? index} className="flex items-center gap-3" style={{ padding: "10px 0", borderTop: `1px solid ${DV2.borderSoft}` }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 800, fontSize: 14 }}>{dayLabel(s)}</div>
                                <div style={{ fontSize: 12, color: DV2.muted }}>Séance {index + 1}</div>
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 700 }}>{clock(s)} à {clock(e)}</div>
                            <div style={{ fontSize: 13, color: DV2.muted, minWidth: 56, textAlign: "right" }}>{Functions.renderDuration((session.durationMinutes ?? 0) / 30)}</div>
                        </div>
                    );
                }) : (
                    <div className="flex items-center gap-3" style={{ padding: "10px 0", borderTop: `1px solid ${DV2.borderSoft}` }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 800, fontSize: 14 }}>{start ? dayLabel(start) : "Date à confirmer"}</div>
                            <div style={{ fontSize: 12, color: DV2.muted }}>{courseFormatLabel(course)}</div>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{start && end ? `${clock(start)} à ${clock(end)}` : ""}</div>
                        <div style={{ fontSize: 13, color: DV2.muted, minWidth: 56, textAlign: "right" }}>{formatHours(courseHours(course))}</div>
                    </div>
                )}
            </Card>

            {course?.sessionDescriptionOverride && (
                <Accordion id="cc2-deroulement" title={`Déroulement ${vocab.ofTheCourse()}`} hint={`Décrit par ${teachrName}.`}>
                    <div style={{ fontSize: 14, color: DV2.textSoft, lineHeight: 1.55, whiteSpace: "pre-line" }}>{course.sessionDescriptionOverride}</div>
                </Accordion>
            )}
            {course?.globalExtraInformation && (
                <Accordion id="cc2-note" title={`Note de ${teachrName}`}>
                    <div style={{ fontSize: 14, color: DV2.textSoft, lineHeight: 1.55 }}>{course.globalExtraInformation}</div>
                </Accordion>
            )}

            {/* Documents du cours */}
            <Card style={{ padding: "16px 20px" }}>
                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>Documents</div>
                <DocumentRow
                    title={quote ? `Devis ${quote.quoteNumber}` : "Devis"}
                    detail={quote ? `${euros(quote.totalAmount)} TTC${quote.status === "active" ? ", en vigueur" : ", remplacé"}` : "Établi à la déclaration, disponible dans vos documents"}
                    action={quote ? <Button label="Ouvrir" variant="secondary" size="sm" onClick={openQuote} /> : null}
                />
                <DocumentRow
                    title="Facture"
                    detail={invoice ? `${invoice.number ?? "Émise"}${invoice.amount != null ? `, ${euros(invoice.amount)}` : ""}` : `Émise après validation ${vocab.ofTheCourse()}, pas encore disponible`}
                    action={invoice ? <Button label="Ouvrir" variant="secondary" size="sm" onClick={() => window.open(invoice.url, "_blank", "noopener")} /> : null}
                />
            </Card>

            <div style={{ fontSize: 13, color: DV2.muted }}>
                Un problème sur {vocab.thisCourse()} ?{" "}
                <TextLink label={`Contacter l'équipe ${brandName}`} onClick={reportIssue} />
            </div>
        </div>
    );
};

export default ClientCourseDetailV2;
